/**
 * Loads Inter (OFL) as raw TTF bytes. jsPDF only skips atob() when the buffer starts with
 * the TrueType sfnt signature 0x00 0x01 0x00 0x00; otherwise invalid base64 triggers atob errors.
 */
import jsPDF from 'jspdf';

const INTER_TTF_URL =
  'https://raw.githubusercontent.com/google/fonts/main/ofl/inter/Inter%5Bopsz%2Cwght%5D.ttf';

const VFS_NAME = 'Inter-Regular.ttf';
const FAMILY = 'Inter';

function arrayBufferToBinaryString(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  const chunk = 8192;
  for (let i = 0; i < bytes.length; i += chunk) {
    const end = Math.min(i + chunk, bytes.length);
    for (let j = i; j < end; j++) {
      binary += String.fromCharCode(bytes[j]);
    }
  }
  return binary;
}

let cachedBinary: string | null | undefined;

async function loadInterBinary(): Promise<string | null> {
  if (cachedBinary !== undefined) return cachedBinary;
  try {
    const res = await fetch(INTER_TTF_URL, { cache: 'force-cache', credentials: 'omit' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    const binary = arrayBufferToBinaryString(buf);
    const ok =
      binary.length >= 4 &&
      binary.charCodeAt(0) === 0 &&
      binary.charCodeAt(1) === 1 &&
      binary.charCodeAt(2) === 0 &&
      binary.charCodeAt(3) === 0;
    if (!ok) throw new Error('Not a TrueType sfnt (expected 0x00 0x01 0x00 0x00)');
    cachedBinary = binary;
    return binary;
  } catch (e) {
    console.warn('[pdf] Could not load Inter TTF for jsPDF:', e);
    cachedBinary = null;
    return null;
  }
}

/**
 * Registers Inter on the document and sets it as the active font. Falls back to Helvetica
 * if the font cannot be loaded (offline, blocked network, etc.).
 */
export async function embedInterFontForPdf(doc: InstanceType<typeof jsPDF>): Promise<boolean> {
  const binary = await loadInterBinary();
  if (!binary) {
    doc.setFont('helvetica', 'normal');
    return false;
  }
  try {
    doc.addFileToVFS(VFS_NAME, binary);
    doc.addFont(VFS_NAME, FAMILY, 'normal');
    doc.addFont(VFS_NAME, FAMILY, 'bold');
    doc.setFont(FAMILY, 'normal');
    return true;
  } catch (e) {
    console.warn('[pdf] addFont failed, using Helvetica:', e);
    doc.setFont('helvetica', 'normal');
    return false;
  }
}
